import PropTypes from 'prop-types'
import { useCallback, useEffect, useState } from 'react'
import styled, { css } from 'styled-components/macro'

import { getToken } from '@loadsmart/loadsmart-ui/dist/theming'

import { ButtonAction as Button } from 'legacy/components/Button/ButtonAction'
import { formikEnhancer } from 'legacy/components/DialogLogin/DialogLogin'
import { FormAlert } from 'legacy/components/FormAlert/FormAlert'
import { I18n } from 'legacy/components/I18n/i18n'
import { Input as InputForm } from 'legacy/components/Input/Input'
import { screen } from 'legacy/styles/screen'

import { useAnalytics } from '../hooks/useAnalytics'
import { ForgotPassword } from './ForgotPassword'
import { LaneBar } from './LaneBar'

const Wrapper = styled.div`
  width: 100%;

  ${screen.md} {
    min-width: 480px;
  }
`

const StyledAlert = styled(FormAlert)`
  margin-bottom: 10px;

  color: ${getToken('color-danger-dark')};

  background-color: ${getToken('color-danger-light')};
`

const StyledButton = styled(Button)`
  margin-bottom: 10px;
`

const Input = styled(InputForm)`
  margin-bottom: 2px;

  ${({ error }) =>
    error &&
    css`
      border: 1px solid ${getToken('color-danger')};
    `};
`

const InputWrapper = styled.div`
  position: relative;

  margin-bottom: 20px;
`

const Label = styled.label`
  display: block;
  margin-bottom: 4px;

  font-weight: 500;
  font-size: 12px;
  line-height: 14px;

  text-transform: uppercase;
`

const Title = styled.header`
  margin: 0;
  margin-bottom: 24px;
  padding-bottom: 20px;

  color: ${getToken('color-neutral-darkest')};
  font-weight: bold;
  font-size: 22px;
  line-height: 24px;
  text-align: center;

  border-bottom: 1px solid ${getToken('color-neutral-light')};
`

const LinkButton = styled(Button)`
  color: ${getToken('color-primary')};
  font-weight: bold;
  text-decoration: underline;

  border: none;
`

const AlignRight = styled.div`
  display: flex;
  flex-direction: row-reverse;

  margin-top: -4px;
  margin-bottom: 16px;
`

const AlignCenter = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

function LoginDialog(props) {
  const {
    values,
    touched,
    errors,
    handleChange,
    handleBlur,
    handleSubmit,
    isSubmitting,
    onClose,
    onToggleLogin,
    loadUUID,
    lane,
  } = props

  const [forgotPassword, setForgotPassord] = useState(false)
  const { analytics } = useAnalytics()
  const title = lane
    ? I18n.get('lb.outletDialog.login.title.preferredLane')
    : I18n.get('lb.outletDialog.login.title')

  useEffect(() => {
    analytics.track(['Outlet Login', 'View'], { loadUUID })
    analytics.tag(['Outlet Login View'])
  }, [analytics, loadUUID])

  const handleForgotPassword = useCallback(() => {
    setForgotPassord(true)
  }, [setForgotPassord])

  const handleCancelForgotPassword = useCallback(() => {
    setForgotPassord(false)
  }, [setForgotPassord])

  if (forgotPassword) {
    return (
      <ForgotPassword
        email={values.email}
        onCancel={handleCancelForgotPassword}
        onClose={onClose}
      />
    )
  }

  return (
    <Wrapper>
      <Title aria-label="Log in at Loadsmart">{title}</Title>
      {lane && (
        <LaneBar origin={lane.origin?.description} destination={lane.destination?.description} />
      )}
      <form name="loginDialog" onSubmit={handleSubmit} id="loginDialog">
        {errors.validity && <StyledAlert message={errors.validity} />}
        <InputWrapper>
          <Label htmlFor="loginDialog-email">{I18n.get('lb.outletDialog.login.email')}</Label>
          <Input
            placeholder={I18n.get('ls.components.LoginDialog.labels.email')}
            id="login-email"
            type="email"
            value={values.email}
            onChange={handleChange}
            onBlur={handleBlur}
            name="email"
            error={errors.email && touched.email}
          />
          {errors.email && touched.email && <StyledAlert message={errors.email} />}
        </InputWrapper>

        <InputWrapper>
          <Label htmlFor="loginDialog-password">{I18n.get('lb.outletDialog.login.password')}</Label>
          <Input
            placeholder={I18n.get('ls.components.LoginDialog.labels.password')}
            type="password"
            value={values.password}
            onChange={handleChange}
            onBlur={handleBlur}
            name="password"
            error={errors.password && touched.password}
          />
          {errors.password && touched.password && <StyledAlert message={errors.password} />}
        </InputWrapper>

        <AlignRight>
          <LinkButton onClick={handleForgotPassword}>
            {I18n.get('lb.outletDialog.forgotPassword.link')}
          </LinkButton>
        </AlignRight>

        <StyledButton
          width="100%"
          type="submit"
          size="sm"
          disabled={isSubmitting}
          activated
          square
          upperCase
        >
          {I18n.get('lb.outletDialog.login.cta')}
        </StyledButton>
        <StyledButton
          width="100%"
          type="button"
          size="sm"
          disabled={isSubmitting}
          onClick={onClose}
          square
          upperCase
        >
          {I18n.get('lb.outletDialog.login.close')}
        </StyledButton>
      </form>
      <AlignCenter>
        <LinkButton onClick={onToggleLogin}>{I18n.get('lb.outletDialog.goToLogin')}</LinkButton>
      </AlignCenter>
    </Wrapper>
  )
}

LoginDialog.propTypes = {
  values: PropTypes.shape({
    email: PropTypes.string,
    password: PropTypes.string,
  }),
  touched: PropTypes.shape({}),
  errors: PropTypes.shape({}),
  handleChange: PropTypes.func,
  handleBlur: PropTypes.func,
  handleSubmit: PropTypes.func,
  onClose: PropTypes.func.isRequired,
  onToggleLogin: PropTypes.func.isRequired,
  isSubmitting: PropTypes.bool,
  loadUUID: PropTypes.string,
  lane: PropTypes.shape({
    origin: PropTypes.shape({
      description: PropTypes.string,
      latitude: PropTypes.number,
      longitude: PropTypes.number,
    }),
    destination: PropTypes.shape({
      description: PropTypes.string,
      latitude: PropTypes.number,
      longitude: PropTypes.number,
    }),
  }),
}

LoginDialog.defaultProps = {
  values: {
    email: '',
    password: '',
  },
  touched: {},
  errors: {},
  handleChange: undefined,
  handleBlur: undefined,
  handleSubmit: undefined,
  isSubmitting: false,
  loadUUID: undefined,
  lane: undefined,
}

LoginDialog.displayName = 'LoginDialog'

const LoginDialogEnhanced = formikEnhancer(LoginDialog)

export { LoginDialogEnhanced as LoginDialog }
